import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { saudeService } from '../../services/saude.service';
import MapaAnatomicoCorporal from './MapaAnatomicoCorporal';
import {
  XMarkIcon,
  UserIcon,
  BriefcaseIcon,
  ShieldExclamationIcon,
  ClockIcon,
  BuildingOffice2Icon,
  HeartIcon,
  SparklesIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  BeakerIcon,
  LightBulbIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  colaboradorId: number;
}

export default function ModalIndividuoFichaClinica({
  isOpen,
  onClose,
  colaboradorId,
}: Props) {
  const [activeTab, setActiveTab] = useState<'anatomia' | 'recomendacoes' | 'coorte' | 'simulador' | 'timeline'>('anatomia');

  const { data: ficha, isLoading, error } = useQuery({
    queryKey: ['individuo-clinica-customer', colaboradorId],
    queryFn: () => saudeService.obterIndividuoClinica(colaboradorId),
    enabled: isOpen && Boolean(colaboradorId),
  });

  const totalAtestados = ficha?.timeline?.filter((t) => t.tipo_evento === 'ATESTADO_MEDICO').length || 0;
  const totalDiasAfastamento = ficha?.timeline?.reduce((acc, t) => acc + (t.dias_afastamento || 0), 0) || 0;
  const scoreSaude = ficha?.recomendacoes?.qualidade_geral_saude?.score ?? 85;
  const scoreAcidente = ficha?.recomendacoes?.potencial_acidente?.score ?? 15;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-gray-200 animate-fadeIn">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center border border-white/20 shadow-inner">
              <UserIcon className="w-6 h-6 text-indigo-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold">
                {isLoading ? 'Carregando Ficha Clínica...' : ficha?.nome || 'Ficha Clínica do Colaborador'}
              </h2>
              <p className="text-xs text-indigo-200 flex items-center gap-2 mt-0.5">
                <span>CPF: {ficha?.cpf || '---'}</span>
                <span>•</span>
                <span>Idade: {ficha?.idade ? `${ficha.idade} anos` : '---'}</span>
                <span>•</span>
                <span>Sexo: {ficha?.sexo || '---'}</span>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Sub-header com Vínculo Ocupacional & Riscos PGR/PCMSO */}
        <div className="px-6 py-3 bg-slate-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-4 text-gray-700">
            <span className="flex items-center gap-1.5 font-medium">
              <BuildingOffice2Icon className="w-4 h-4 text-gray-400" />
              {ficha?.empresa_nome || 'Empresa'}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1.5 font-semibold text-indigo-900">
              <BriefcaseIcon className="w-4 h-4 text-indigo-500" />
              {ficha?.setor_nome} / {ficha?.cargo_nome}
            </span>
            {ficha?.tempo_casa_meses !== undefined && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1.5 text-gray-500">
                  <ClockIcon className="w-4 h-4 text-gray-400" />
                  {ficha.tempo_casa_meses} meses de empresa
                </span>
              </>
            )}
          </div>

          {/* Riscos Ocupacionais Vigentes */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
              <ShieldExclamationIcon className="w-3.5 h-3.5 text-amber-500" />
              Riscos PGR:
            </span>
            {ficha?.riscos_ocupacionais_vigentes && ficha.riscos_ocupacionais_vigentes.length > 0 ? (
              ficha.riscos_ocupacionais_vigentes.map((r, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-200"
                >
                  {r.nome}
                </span>
              ))
            ) : (
              <span className="text-gray-400 italic">Nenhum risco crítico mapeado</span>
            )}
          </div>
        </div>

        {/* 4 Cards de Resumo Clínico Superior */}
        <div className="px-6 py-3.5 bg-white border-b border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {/* Score de Saúde Individual */}
          <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
            <span className="text-[10px] font-bold uppercase text-emerald-800 flex items-center gap-1">
              <HeartIcon className="w-3.5 h-3.5 text-emerald-600" />
              Saúde Individual
            </span>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className="text-2xl font-extrabold text-emerald-950 font-mono">
                {scoreSaude}
              </span>
              <span className="text-[10px] text-emerald-700 font-semibold">/100</span>
            </div>
            <span className="text-[10px] font-bold text-emerald-800 uppercase">
              {ficha?.recomendacoes?.qualidade_geral_saude?.classificacao || 'BOA'}
            </span>
          </div>

          {/* Potencial de Acidente Ocupacional */}
          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
            <span className="text-[10px] font-bold uppercase text-amber-800 flex items-center gap-1">
              <ShieldExclamationIcon className="w-3.5 h-3.5 text-amber-600" />
              Potencial de Acidente
            </span>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className="text-2xl font-extrabold text-amber-950 font-mono">
                {scoreAcidente}%
              </span>
            </div>
            <span className="text-[10px] font-bold text-amber-800 uppercase">
              Grau {ficha?.recomendacoes?.potencial_acidente?.classificacao || 'BAIXO'}
            </span>
          </div>

          {/* Dias de Afastamento Acumulados */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
            <span className="text-[10px] font-bold uppercase text-slate-600 flex items-center gap-1">
              <ClockIcon className="w-3.5 h-3.5 text-slate-500" />
              Afastamentos
            </span>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className="text-2xl font-extrabold text-slate-900 font-mono">
                {totalDiasAfastamento}
              </span>
              <span className="text-[10px] text-slate-500">dias</span>
            </div>
            <span className="text-[10px] text-slate-600">
              {totalAtestados} atestado(s)
            </span>
          </div>

          {/* Status Geral de Aptidão */}
          <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-200">
            <span className="text-[10px] font-bold uppercase text-indigo-800 flex items-center gap-1">
              <ShieldCheckIcon className="w-3.5 h-3.5 text-indigo-600" />
              Status PCMSO
            </span>
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                <CheckCircleIcon className="w-3.5 h-3.5" />
                Apto Vigente
              </span>
            </div>
            <span className="text-[10px] text-indigo-700 block mt-1">
              {ficha?.achados_clinicos?.length || 0} achado(s) monitorado(s)
            </span>
          </div>
        </div>

        {/* Abas de Navegação */}
        <div className="px-6 border-b border-gray-200 bg-white flex items-center gap-2 overflow-x-auto text-xs font-semibold">
          <button
            onClick={() => setActiveTab('anatomia')}
            className={`py-3 px-3.5 border-b-2 flex items-center gap-1.5 transition-colors whitespace-nowrap ${
              activeTab === 'anatomia'
                ? 'border-indigo-600 text-indigo-700 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <SparklesIcon className="w-4 h-4" />
            1. Mapa Anatômico 360° ({ficha?.achados_clinicos?.length || 0})
          </button>

          <button
            onClick={() => setActiveTab('recomendacoes')}
            className={`py-3 px-3.5 border-b-2 flex items-center gap-1.5 transition-colors whitespace-nowrap ${
              activeTab === 'recomendacoes'
                ? 'border-indigo-600 text-indigo-700 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BeakerIcon className="w-4 h-4" />
            2. Hábitos, Ergonomia & Fitoterapia
          </button>

          <button
            onClick={() => setActiveTab('coorte')}
            className={`py-3 px-3.5 border-b-2 flex items-center gap-1.5 transition-colors whitespace-nowrap ${
              activeTab === 'coorte'
                ? 'border-indigo-600 text-indigo-700 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ChartBarIcon className="w-4 h-4" />
            3. Comparativo de Coorte
          </button>

          <button
            onClick={() => setActiveTab('simulador')}
            className={`py-3 px-3.5 border-b-2 flex items-center gap-1.5 transition-colors whitespace-nowrap ${
              activeTab === 'simulador'
                ? 'border-indigo-600 text-indigo-700 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ArrowTrendingUpIcon className="w-4 h-4" />
            4. Simulador de Propensões (3 Cenários)
          </button>

          <button
            onClick={() => setActiveTab('timeline')}
            className={`py-3 px-3.5 border-b-2 flex items-center gap-1.5 transition-colors whitespace-nowrap ${
              activeTab === 'timeline'
                ? 'border-indigo-600 text-indigo-700 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ClockIcon className="w-4 h-4" />
            5. Linha do Tempo ({ficha?.timeline?.length || 0})
          </button>
        </div>

        {/* Modal Body com Scroll */}
        <div className="p-6 overflow-y-auto flex-1 bg-slate-50/50 space-y-6">
          {isLoading ? (
            <div className="py-20 text-center text-sm text-gray-500">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              Carregando perfil clínico completo e mapa anatômico...
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 text-red-800 border border-red-200 rounded-xl text-xs">
              Erro ao carregar dados clínicos do colaborador. Tente novamente mais tarde.
            </div>
          ) : (
            <>
              {/* ABA 1: MAPA ANATÔMICO 360 */}
              {activeTab === 'anatomia' && (
                <MapaAnatomicoCorporal
                  achados={ficha?.achados_clinicos || []}
                  analiseFisionomica={ficha?.analise_fisionomica}
                  scoreQualidade={scoreSaude}
                  scoreAcidente={scoreAcidente}
                />
              )}

              {/* ABA 2: RECOMENDAÇÕES, HÁBITOS & TRATAMENTOS POPULARES */}
              {activeTab === 'recomendacoes' && (
                <div className="space-y-6">
                  {/* Hábitos e Cuidados Posturais */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm space-y-3">
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-emerald-600" />
                        Hábitos & Estilo de Vida Saudável
                      </h4>
                      <ul className="space-y-2 text-xs text-gray-700">
                        {(ficha?.recomendacoes?.habitos_estilo_vida || [
                          'Manter ingestão hídrica mínima de 2.5 litros ao longo da jornada.',
                          'Realizar pausas regulares de 5 minutos a cada 60 minutos de esforço contínuo.',
                          'Prática de caminhada leve de 30 minutos diários para condicionamento cardiorrespiratório.',
                        ]).map((h, idx) => (
                          <li key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-emerald-50/50 border border-emerald-100">
                            <span className="text-emerald-600 font-bold">•</span>
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm space-y-3">
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                        <LightBulbIcon className="w-4 h-4 text-indigo-600" />
                        Cuidados Posturais & Ergonomia (NR-17)
                      </h4>
                      <ul className="space-y-2 text-xs text-gray-700">
                        {(ficha?.recomendacoes?.cuidados_posturais_ergonomicos || [
                          'Ajustar altura de bancada de trabalho para manter cotovelos a 90 graus.',
                          'Não flexionar o tronco sem dobrar os joelhos ao levantar cargas acima de 15 kg.',
                          'Utilizar suporte ergonômico lombar e apoio de punho para tarefas repetitivas.',
                        ]).map((c, idx) => (
                          <li key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-indigo-50/50 border border-indigo-100">
                            <span className="text-indigo-600 font-bold">•</span>
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Tratamentos Populares e Fitoterapia Sugerida com Regras e Contraindicações */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm space-y-4">
                    <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                          <BeakerIcon className="w-4 h-4 text-emerald-600" />
                          Tratamentos Tradicionais & Fitoterapia Sugerida
                        </h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Práticas integrativas e complementares com dosagens recomendadas e alertas de segurança.
                        </p>
                      </div>
                      <span className="text-[11px] font-bold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                        Uso Consciente
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(ficha?.recomendacoes?.tratamentos_populares_sugeridos || [
                        {
                          problema_alvo: 'Tensão Muscular Cervical e Estresse',
                          tratamento_ou_fitoterapico: 'Infusão de Camomila (Matricaria recutita) com Melissa',
                          orientacao_uso: '1 xícara morna 30 minutos antes do repouso noturno.',
                          alerta_contraindicacao: 'Evitar associação com ansiolíticos sedativos sem orientação médica.',
                        },
                        {
                          problema_alvo: 'Desconforto Lombar Pós-Jornada',
                          tratamento_ou_fitoterapico: 'Compressa Morna com Extrato de Arnica (Arnica montana)',
                          orientacao_uso: 'Aplicação local por 15 minutos ao término da jornada de trabalho.',
                          alerta_contraindicacao: 'Uso estritamente tópico; não aplicar sobre pele lesionada.',
                        },
                      ]).map((item, idx) => (
                        <div
                          key={idx}
                          className="p-4 rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/40 via-white to-white space-y-2 shadow-2xs"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded uppercase">
                              {item.problema_alvo}
                            </span>
                          </div>
                          <h5 className="text-xs font-bold text-gray-900">
                            {item.tratamento_ou_fitoterapico}
                          </h5>
                          <p className="text-xs text-gray-600">
                            <strong>Posologia Sugerida:</strong> {item.orientacao_uso}
                          </p>
                          {item.alerta_contraindicacao && (
                            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-800 flex items-start gap-1.5">
                              <ExclamationCircleIcon className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                              <span><strong>Atenção / Contraindicação:</strong> {item.alerta_contraindicacao}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ABA 3: COMPARATIVO DE COORTE */}
              {activeTab === 'coorte' && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4">
                  <div className="pb-3 border-b border-gray-100">
                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <ChartBarIcon className="w-4 h-4 text-indigo-600" />
                      Benchmarking Populacional & Comparativo de Coorte
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Comparação do score individual frente a grupos pares da mesma faixa etária, mesmo setor e tempo de casa.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Faixa Etária */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-center space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-500 block">
                        Mesma Faixa Etária ({ficha?.comparativo_coorte?.mesma_faixa_etaria.faixa || '36-45 anos'})
                      </span>
                      <div className="text-2xl font-extrabold text-indigo-900 font-mono">
                        {ficha?.comparativo_coorte?.mesma_faixa_etaria.score_individuo ?? 85}
                      </div>
                      <span className="text-xs text-slate-600 block">
                        Média da coorte: <strong>{ficha?.comparativo_coorte?.mesma_faixa_etaria.media_coorte ?? 78}</strong>
                      </span>
                      <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        {ficha?.comparativo_coorte?.mesma_faixa_etaria.status || 'MELHOR'} (Percentil {ficha?.comparativo_coorte?.mesma_faixa_etaria.percentil ?? 82}%)
                      </span>
                    </div>

                    {/* Mesmo Setor */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-center space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-500 block">
                        Mesmo Setor ({ficha?.comparativo_coorte?.mesmo_setor_empresa.setor_nome || 'Operação'})
                      </span>
                      <div className="text-2xl font-extrabold text-indigo-900 font-mono">
                        {ficha?.comparativo_coorte?.mesmo_setor_empresa.score_individuo ?? 85}
                      </div>
                      <span className="text-xs text-slate-600 block">
                        Média do setor: <strong>{ficha?.comparativo_coorte?.mesmo_setor_empresa.media_coorte ?? 80}</strong>
                      </span>
                      <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        {ficha?.comparativo_coorte?.mesmo_setor_empresa.status || 'MELHOR'} (Percentil {ficha?.comparativo_coorte?.mesmo_setor_empresa.percentil ?? 78}%)
                      </span>
                    </div>

                    {/* Mesmo Tempo de Casa */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-center space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-500 block">
                        Tempo de Casa ({ficha?.comparativo_coorte?.mesmo_tempo_casa.faixa_tempo || '1-3 anos'})
                      </span>
                      <div className="text-2xl font-extrabold text-indigo-900 font-mono">
                        {ficha?.comparativo_coorte?.mesmo_tempo_casa.score_individuo ?? 85}
                      </div>
                      <span className="text-xs text-slate-600 block">
                        Média pares: <strong>{ficha?.comparativo_coorte?.mesmo_tempo_casa.media_coorte ?? 82}</strong>
                      </span>
                      <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        {ficha?.comparativo_coorte?.mesmo_tempo_casa.status || 'MELHOR'} (Percentil {ficha?.comparativo_coorte?.mesmo_tempo_casa.percentil ?? 74}%)
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA 4: SIMULADOR PREDITIVO DE PROPENSÕES (3 CENÁRIOS) */}
              {activeTab === 'simulador' && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4">
                  <div className="pb-3 border-b border-gray-100">
                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <ArrowTrendingUpIcon className="w-4 h-4 text-indigo-600" />
                      Simulação Preditiva de Propensões de Saúde (12 Meses)
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Projeção estatística baseada no histórico de exames, questionários e assiduidade.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Cenário Otimista */}
                    <div className="p-4 rounded-xl border border-emerald-300 bg-gradient-to-b from-emerald-50/60 to-white space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase">
                          Cenário Otimista
                        </span>
                        <span className="text-xs font-mono font-bold text-emerald-700">
                          {ficha?.simulador_panoramas?.otimista.probabilidade_afastamento_12m ?? 3.5}% prob.
                        </span>
                      </div>
                      <h5 className="text-xs font-bold text-emerald-950">
                        {ficha?.simulador_panoramas?.otimista.descricao || 'Adesão plena às orientações preventivas'}
                      </h5>
                      <p className="text-[11px] text-gray-600">
                        Estimativa de dias ganhos em bem-estar e produtividade: <strong>+{ficha?.simulador_panoramas?.otimista.estimativa_dias_ganhos ?? 8} dias</strong>.
                      </p>
                      <ul className="text-[11px] text-emerald-900 space-y-1 pt-1 border-t border-emerald-100">
                        {(ficha?.simulador_panoramas?.otimista.beneficios_esperados || [
                          'Estabilização de queixas posturais',
                          'Zero ocorrências de afastamento por DORT',
                        ]).map((b, i) => (
                          <li key={i} className="flex items-center gap-1.5">
                            <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-600" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Cenário Realista */}
                    <div className="p-4 rounded-xl border border-blue-300 bg-gradient-to-b from-blue-50/60 to-white space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800 uppercase">
                          Cenário Realista
                        </span>
                        <span className="text-xs font-mono font-bold text-blue-700">
                          {ficha?.simulador_panoramas?.realista.probabilidade_afastamento_12m ?? 8.2}% prob.
                        </span>
                      </div>
                      <h5 className="text-xs font-bold text-blue-950">
                        {ficha?.simulador_panoramas?.realista.descricao || 'Manutenção do padrão ocupacional atual'}
                      </h5>
                      <p className="text-[11px] text-gray-600">
                        Dias estimados de afastamento moderado no ano: <strong>{ficha?.simulador_panoramas?.realista.estimativa_dias_afastamento ?? 2} dias</strong>.
                      </p>
                      <ul className="text-[11px] text-blue-900 space-y-1 pt-1 border-t border-blue-100">
                        {(ficha?.simulador_panoramas?.realista.riscos_esperados || [
                          'Oscilações esporádicas de tensão cervical',
                          'Demanda de pausas ergonômicas ativas',
                        ]).map((r, i) => (
                          <li key={i} className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Cenário Pessimista */}
                    <div className="p-4 rounded-xl border border-rose-300 bg-gradient-to-b from-rose-50/60 to-white space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rose-100 text-rose-800 uppercase">
                          Cenário Pessimista
                        </span>
                        <span className="text-xs font-mono font-bold text-rose-700">
                          {ficha?.simulador_panoramas?.pessimista.probabilidade_afastamento_12m ?? 24.0}% prob.
                        </span>
                      </div>
                      <h5 className="text-xs font-bold text-rose-950">
                        {ficha?.simulador_panoramas?.pessimista.descricao || 'Abandono de pausas e piora de queixas'}
                      </h5>
                      <p className="text-[11px] text-gray-600">
                        Risco de afastamento prolongado: <strong>{ficha?.simulador_panoramas?.pessimista.estimativa_dias_afastamento ?? 14} dias</strong>.
                      </p>
                      <ul className="text-[11px] text-rose-900 space-y-1 pt-1 border-t border-rose-100">
                        {(ficha?.simulador_panoramas?.pessimista.complicacoes_potenciais || [
                          'Agravamento de lombalgia com indicação pericial',
                          'Gatilho potencial para NTEP Previdenciário',
                        ]).map((c, i) => (
                          <li key={i} className="flex items-center gap-1.5">
                            <ExclamationCircleIcon className="w-3.5 h-3.5 text-rose-600" />
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA 5: TIMELINE LONGITUDINAL */}
              {activeTab === 'timeline' && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4">
                  <div className="pb-3 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <ClockIcon className="w-4 h-4 text-indigo-600" />
                        Linha do Tempo Longitudinal Unificada
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Todos os eventos clínicos, exames periódicos e atestados médicos do colaborador.
                      </p>
                    </div>
                    <span className="text-xs font-bold text-indigo-800 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-200">
                      {ficha?.timeline?.length || 0} Registros
                    </span>
                  </div>

                  <div className="space-y-3 relative pl-6 border-l-2 border-indigo-200">
                    {(ficha?.timeline || []).map((ev) => (
                      <div key={ev.id} className="relative group">
                        <span
                          className={`absolute -left-[31px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-white shadow-xs ${
                            ev.tipo_evento === 'ATESTADO_MEDICO' ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                        />
                        <div className="p-3 bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-200 transition-colors">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-gray-900">
                              {ev.titulo}
                            </span>
                            <span className="text-[11px] text-gray-500 font-mono">
                              {new Date(ev.data_evento).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                          {ev.subtitulo && (
                            <p className="text-xs text-gray-600 mt-0.5">{ev.subtitulo}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                ev.tipo_evento === 'ATESTADO_MEDICO'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-emerald-100 text-emerald-800'
                              }`}
                            >
                              {ev.parecer_ou_status}
                            </span>
                            {ev.dias_afastamento > 0 && (
                              <span className="text-xs font-bold text-rose-600 font-mono">
                                {ev.dias_afastamento} dias de afastamento
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
          <span>Prontuário Médico Digital • Protegido por Sigilo e LGPD</span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold transition"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
